import { motion } from "framer-motion";
import { ArrowRight, Leaf, Sparkles, Trees, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const highlights = [
  { icon: Leaf, label: "Less landfill", desc: "Hints reduce waste by helping every gift land with the right person.", iconBg: "bg-destructive/15", iconColor: "text-destructive" },
  { icon: ShieldCheck, label: "More intention", desc: "People buy with confidence instead of defaulting to generic fillers.", iconBg: "bg-foreground/10", iconColor: "text-foreground" },
  { icon: Trees, label: "Greener fulfilment", desc: "Our physical card experience is designed with lower-waste choices in mind.", iconBg: "bg-[hsl(45,90%,50%)]/20", iconColor: "text-[hsl(45,90%,50%)]" },
];

const GivingBackTeaser = () => {
  return (
    <section className="px-4 py-20 bg-background/70">
      <div className="container max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Sparkles className="mx-auto mb-4 h-10 w-10 text-[hsl(45,90%,50%)]" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Giving back, together
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto">
            Gifting shouldn&apos;t cost the Earth. Every year, millions of unwanted gifts end up in landfill. By dropping a hint, you help ensure every gift has a home, a purpose, and a person who loves it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
          {highlights.map((h, i) => (
            <motion.div
              key={h.label}
              className="premium-panel rounded-[1.75rem] p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className={`w-12 h-12 rounded-xl ${h.iconBg} flex items-center justify-center mx-auto mb-3`}>
                <h.icon className={`h-6 w-6 ${h.iconColor}`} />
              </div>
              <h3 className="font-bold text-foreground mb-1">{h.label}</h3>
              <p className="text-sm text-muted-foreground">{h.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Commitments */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-2xl mx-auto">
          <motion.div
            className="premium-panel flex items-center gap-3 rounded-2xl p-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/20">
              <Leaf className="h-5 w-5 text-destructive" />
            </div>
            <div className="text-left">
              <p className="font-bold text-sm text-foreground">Climate commitment</p>
              <p className="text-xs text-muted-foreground">0.5% of revenue goes to climate initiatives</p>
            </div>
          </motion.div>
          <motion.div
            className="premium-panel flex items-center gap-3 rounded-2xl p-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-10 h-10 rounded-full bg-[hsl(45,90%,50%)]/20 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-[hsl(45,90%,50%)]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-sm text-foreground">0% commission on charity gifts</p>
              <p className="text-xs text-muted-foreground">Every dollar goes directly to the cause</p>
            </div>
          </motion.div>
        </div>

        {/* Eco card supplier */}
        <motion.div
          className="premium-panel mx-auto mb-10 flex max-w-2xl items-start gap-3 rounded-2xl p-5 text-left"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground/10">
            <Trees className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <p className="font-bold text-sm text-foreground mb-1">Eco-friendly card supplier</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Even our card supplier is considered eco-friendly. They operate on a zero-waste, print-on-demand model using FSC-certified or recycled paper stocks. Cards are printed locally to the recipient to reduce shipping emissions, and they plant 5 trees for every 100 cards sold to offset their carbon footprint.
            </p>
          </div>
        </motion.div>

        <Button asChild className="bg-destructive text-destructive-foreground shadow-lg shadow-destructive/20 hover:shadow-xl hover:shadow-destructive/30 hover:brightness-110 active:scale-[0.98] text-base px-8 py-3 rounded-full font-semibold uppercase tracking-[0.18em] gap-2">
          <Link to="/community">
            Explore organisations <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
};

export default GivingBackTeaser;
